<template>
  <div class="property-tab">
    <div class="appearance-grid">
      <span class="appearance-label">Background:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" class="overflow-x-hidden" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn size="small" :color="noteColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Background Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="noteColor" class="overflow-hidden" elevation="15" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
      </div>
      <span class="appearance-label">Text:</span>
      <div class="appearance-controls">
        <v-menu location="right" :close-on-content-click="false" @update:modelValue="colorPickerHandler">
          <template #activator="{ props: menu }">
            <v-tooltip location="bottom">
              <template #activator="{ props: tooltip }">
                <v-btn size="small" :color="textColor" v-bind="{ ...menu, ...tooltip }">
                  <v-icon>mdi-format-color-fill</v-icon>
                </v-btn>
              </template>
              <span>Change Note Text Color</span>
            </v-tooltip>
          </template>
          <v-color-picker v-model="textColor" elevation="15" class="overflow-hidden" mode="rgba" :class="{ disabled: PreferenceStore.INSTANCE.isReadOnly }"></v-color-picker>
        </v-menu>
        <v-slider
v-model="fontSize" class="ml-2" hide-details min="6" max="26" step="1"
          track-fill-color="primary" track-color="grey" thumb-color="primary"
          :disabled="PreferenceStore.INSTANCE.isReadOnly"
          v-on="{ mousedown: () => startBatchCommand(), mouseup: () => stopBatchCommand() }"></v-slider>
        <span class="slider-value">{{ fontSize }}</span>
      </div>
      <span class="appearance-label">Style:</span>
      <div class="appearance-controls">
        <v-btn-toggle v-model="textStyle" density="compact" multiple :border="true" variant="tonal" color="primary">
          <v-tooltip location="bottom">
            <template #activator="{ props }"><v-btn size="x-small" v-bind="props" :disabled="PreferenceStore.INSTANCE.isReadOnly"><v-icon>mdi-format-bold</v-icon></v-btn></template>
            <span>Bold</span>
          </v-tooltip>
          <v-tooltip location="bottom">
            <template #activator="{ props }"><v-btn size="x-small" v-bind="props" :disabled="PreferenceStore.INSTANCE.isReadOnly"><v-icon>mdi-format-italic</v-icon></v-btn></template>
            <span>Italic</span>
          </v-tooltip>
          <v-tooltip location="bottom">
            <template #activator="{ props }"><v-btn size="x-small" v-bind="props" :disabled="PreferenceStore.INSTANCE.isReadOnly"><v-icon>mdi-format-underline</v-icon></v-btn></template>
            <span>Underline</span>
          </v-tooltip>
        </v-btn-toggle>
      </div>
    </div>
    <div class="appearance-footer">
      <v-btn size="small" color="primary" :disabled="PreferenceStore.INSTANCE.isReadOnly" @click="resetDefaults()">Reset Defaults</v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Note } from "../mvc/note";
import { ui } from "@clientio/rappid";
import RappidContext from "../rappid/rappid-context";
import { computed, inject, onBeforeUnmount, onMounted, ref } from "vue";
import { PreferenceStore } from "../services/preference-store";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const currentSelection = ref<any>({});
const colorPicking = ref<boolean>(false);

const compProps = defineProps<{ model: Note }>();
const emit = defineEmits(["notifyChange"]);

const rappidContext: RappidContext = inject<RappidContext>("rappidContext");

const changeNotifier = (model) => {
  emit("notifyChange", model);
};

onMounted(() => {
  ui.TextEditor.on("select:changed", (start, end) => {
    currentSelection.value = { start: start, end: end };
  });
  compProps.model.on("change", changeNotifier, compProps.model);
});

onBeforeUnmount(() => {
  if (colorPicking.value) {
    stopBatchCommand();
  }
  compProps.model.off("change", changeNotifier);
});

function colorPickerHandler(value: boolean) {
  colorPicking.value = value;
  if (value) {
    startBatchCommand();
  } else {
    stopBatchCommand();
  }
}

function startBatchCommand() {
  rappidContext.commandManager.initBatchCommand();
}

function stopBatchCommand() {
  rappidContext.commandManager.storeBatchCommand();
}

onBeforeUnmount(() => {
  stopBatchCommand();
});

function resetDefaults() {
  startBatchCommand();
  compProps.model.resetDefaults();
  stopBatchCommand();
}

const noteColor = computed({
  get(): string {
    return compProps.model.getFillColor();
  },

  set(color: string) {
    compProps.model.setFillColor(color);
  },
});

const textColor = computed({
  get(): string {
    currentSelection.value;
    return compProps.model.getTextColor();
  },

  set(color: string) {
    compProps.model.setTextColor(color);
  },
});

const textBold = computed({
  get(): boolean {
    currentSelection.value;
    return compProps.model.getTextBold();
  },

  set(enabled: boolean) {
    compProps.model.setTextBold(enabled);
  },
});

const textItalic = computed({
  get(): boolean {
    currentSelection.value;
    return compProps.model.getTextItalic();
  },

  set(enabled: boolean) {
    compProps.model.setTextItalic(enabled);
  },
});

const textUnderline = computed({
  get(): boolean {
    currentSelection.value;
    return compProps.model.getTextUnderline();
  },

  set(enabled: boolean) {
    compProps.model.setTextUnderline(enabled);
  },
});

const textAlign = computed({
  get(): number {
    currentSelection.value;
    return compProps.model.getTextAlign();
  },

  set(option: number) {
    compProps.model.setTextAlign(option);
  },
});

const fontSize = computed({
  get(): number {
    currentSelection.value;
    return compProps.model.getFontSize();
  },

  set(size: number) {
    compProps.model.setFontSize(size);
  },
});

const textStyle = computed({
  get() {
    const styles: number[] = [];
    if (textBold.value) styles.push(0);
    if (textItalic.value) styles.push(1);
    if (textUnderline.value) styles.push(2);
    return styles;
  },

  set(styles: number[]) {
    startBatchCommand();
    textBold.value = styles.includes(0);
    textItalic.value = styles.includes(1);
    textUnderline.value = styles.includes(2);
    stopBatchCommand();
  },
});
</script>
